import { type ReactNode, useCallback, useEffect, useState } from 'react'
import type { OfferDecision, OfferDoc, OfferLang } from '../../lib/offers'
import { WECHAT_GREEN, WeChatGlyph } from './WeChatSheet'
import { type OfferReply, fetchOffer, sendDecision } from './api'

// The page a forwarder's client opens from an offer link (/offer/:id). Phone first, because most open it
// inside WeChat. It reads in Chinese there, or on a Chinese phone, and in English elsewhere, with a
// switch. The client proves who they are with WeChat (a simulated consent screen, labelled as such) or by
// email, confirms or declines once, and gets the audit record. The seller sees it on their desk.
//
// Shared by the product (freightratesmcp.com/offer/:id) and the partner demo. Everything that names who
// delivers the offer comes in as `brand`, so neither app's name is written into the other's page.

/** Who the page says the offer comes through. */
export interface OfferPageBrand {
  /** The top-left mark. Gets the offer once it has loaded, null before. */
  mark: (doc: OfferDoc | null) => ReactNode
  /** The small line at the foot of the page. */
  powered: Record<OfferLang, string>
  /** The app name the WeChat consent screen asks on behalf of, for this forwarder. */
  app: (member: string) => string
  /** The line under the WeChat consent screen saying it is simulated. */
  simulated: Record<OfferLang, string>
}

const PORTS: Record<string, [string, string]> = {
  CNSHA: ['Shanghai', '上海'],
  CNNGB: ['Ningbo', '宁波'],
  CNNSA: ['Nansha', '南沙'],
  CNSZX: ['Shenzhen', '深圳'],
  CNXMN: ['Xiamen', '厦门'],
  CNTAO: ['Qingdao', '青岛'],
  VNSGN: ['Ho Chi Minh City', '胡志明市'],
  NLRTM: ['Rotterdam', '鹿特丹'],
  DEHAM: ['Hamburg', '汉堡'],
  GBFXT: ['Felixstowe', '费利克斯托'],
  USLAX: ['Los Angeles', '洛杉矶'],
  USNYC: ['New York', '纽约'],
  ITGOA: ['Genoa', '热那亚'],
  PLGDN: ['Gdansk', '格但斯克'],
  INMUN: ['Mundra', '蒙德拉'],
  INNSA: ['Nhava Sheva', '那瓦舍瓦'],
}
const portName = (code: string, lang: OfferLang, names: Record<string, string>) =>
  PORTS[code]?.[lang === 'zh' ? 1 : 0] ?? names[code] ?? code

const CHARGE_ZH: [RegExp, string][] = [
  [/^ocean freight/i, '海运费'],
  [/^origin terminal/i, '起运港码头操作费'],
  [/^destination terminal/i, '目的港码头操作费'],
  [/customs/i, '报关费'],
  [/bill of lading|^b\/l/i, '提单费'],
  [/ams|isf/i, 'AMS/ISF 申报费'],
  [/^delivery/i, '送货费'],
  [/insurance/i, '保险费'],
  [/documentation|^docs?\b/i, '单证费'],
  [/^handling/i, '操作费'],
  [/commission/i, '代理佣金'],
  [/^thc\b/i, '码头操作费'],
  [/trucking|haulage/i, '拖车费'],
]
const chargeZh = (name: string) => CHARGE_ZH.find(([re]) => re.test(name))?.[1]

const T = {
  en: {
    kicker: 'Freight offer',
    for: 'For',
    from: 'from',
    at: 'at',
    sample:
      'Sample offer. Try confirming it: the sample resets within a day and no booking is made.',
    validUntil: 'Valid until',
    daysLeft: (n: number) => (n <= 1 ? 'last day' : `${n} days left`),
    charges: 'Charges',
    total: 'Total',
    next: 'When you confirm',
    steps: (m: string) => [
      `${m} books the space with the line.`,
      'You receive the booking confirmation and the documents list.',
      'You can track every box from loading to delivery.',
    ],
    confirm: 'Confirm offer',
    decline: 'Decline',
    who: 'Who is confirming?',
    whoDecline: 'Who is declining?',
    withWechat: 'Continue with WeChat',
    orEmail: 'or with your work email',
    name: 'Your name',
    company: 'Company',
    email: 'Work email',
    po: 'Your PO or reference (optional)',
    reason: 'Reason',
    reasons: ['Price is too high', 'Timing does not work', 'We chose another forwarder', 'Other'],
    reasonNote: 'Anything the forwarder should know (optional)',
    back: 'Back',
    send: 'Confirm offer',
    sendDecline: 'Send decline',
    sending: 'Sending…',
    wxTitle: 'WeChat sign-in',
    wxAsks: (app: string) =>
      `${app} would like to use your WeChat public profile (name and photo).`,
    wxName: 'Your WeChat name',
    wxAllow: 'Allow',
    wxDeny: 'Deny',
    signedIn: 'Signed in with WeChat as',
    confirmed: 'Offer confirmed',
    declined: 'Offer declined',
    expired: 'This offer has expired',
    expiredBody: (s: string) => `Ask ${s} for a new offer with today’s prices.`,
    record: 'Audit record',
    by: 'By',
    via: { wechat: 'WeChat sign-in', email: 'email' },
    when: 'When',
    ref: 'Reference',
    poShort: 'PO',
    notified: (m: string) => `${m} has been notified.`,
    print: 'Print or save as PDF',
    contact: 'Questions? Contact',
    notFound: 'This offer link is not valid',
    notFoundBody:
      'It may have been mistyped or replaced by a newer version. Ask your forwarder for the current link.',
    loading: 'Opening the offer…',
    error: 'Something went wrong. Please try again.',
  },
  zh: {
    kicker: '运价报价',
    for: '客户',
    from: '报价人',
    at: '',
    sample: '示例报价：可以试着确认。示例会在一天内重置，不会产生实际订舱。',
    validUntil: '有效期至',
    daysLeft: (n: number) => (n <= 1 ? '今天截止' : `还剩 ${n} 天`),
    charges: '费用明细',
    total: '合计',
    next: '确认之后',
    steps: (m: string) => [
      `${m} 向船公司订舱。`,
      '您将收到订舱确认和单证清单。',
      '从装柜到交付，每个集装箱都可以实时跟踪。',
    ],
    confirm: '确认报价',
    decline: '拒绝',
    who: '请确认您的身份',
    whoDecline: '请确认您的身份',
    withWechat: '微信一键登录',
    orEmail: '或使用工作邮箱',
    name: '您的姓名',
    company: '公司',
    email: '工作邮箱',
    po: '您的采购单号或备注（可选）',
    reason: '原因',
    reasons: ['价格偏高', '时间不合适', '已选择其他货代', '其他'],
    reasonNote: '想告诉货代的话（可选）',
    back: '返回',
    send: '确认报价',
    sendDecline: '提交',
    sending: '提交中…',
    wxTitle: '微信授权登录',
    wxAsks: (app: string) => `${app} 申请获取您的微信公开信息（昵称、头像）。`,
    wxName: '微信昵称',
    wxAllow: '允许',
    wxDeny: '拒绝',
    signedIn: '已通过微信登录：',
    confirmed: '报价已确认',
    declined: '已拒绝报价',
    expired: '报价已过期',
    expiredBody: (s: string) => `请联系 ${s} 获取最新报价。`,
    record: '确认记录',
    by: '确认人',
    via: { wechat: '微信登录', email: '邮箱' },
    when: '时间',
    ref: '报价编号',
    poShort: '采购单号',
    notified: (m: string) => `已通知 ${m}。`,
    print: '打印或保存为 PDF',
    contact: '如有疑问，请联系',
    notFound: '报价链接无效',
    notFoundBody: '链接可能有误，或已被新版本替代。请向货代索取最新链接。',
    loading: '正在打开报价…',
    error: '出了点问题，请重试。',
  },
}

const initialLang = (): OfferLang => {
  try {
    const saved = localStorage.getItem('offer-lang')
    if (saved === 'zh' || saved === 'en') return saved
  } catch {
    // No storage: fall through to the browser's language.
  }
  if (/MicroMessenger/i.test(navigator.userAgent)) return 'zh'
  return navigator.language?.toLowerCase().startsWith('zh') ? 'zh' : 'en'
}

const fmtMoney = (n: number, cur: string) =>
  `${cur} ${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
const fmtDate = (iso: string, lang: OfferLang) =>
  new Date(iso).toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
const fmtTime = (iso: string, lang: OfferLang) =>
  new Date(iso).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  })

type Step =
  | { kind: 'idle' }
  | { kind: 'who'; action: 'confirmed' | 'declined' }
  | { kind: 'wechat'; action: 'confirmed' | 'declined' }
  | { kind: 'details'; action: 'confirmed' | 'declined'; id: Identity }

type Identity =
  | { via: 'wechat'; name: string }
  | { via: 'email'; name: string; email: string; company: string }

export function OfferPage({
  id,
  brand,
  portNames = {},
}: {
  id: string
  brand: OfferPageBrand
  /** More UN/LOCODE to English port names, for lanes the page's own list does not know. */
  portNames?: Record<string, string>
}) {
  const [lang, setLangState] = useState<OfferLang>(initialLang)
  const t = T[lang]
  const [offer, setOffer] = useState<OfferReply | null>(null)
  const [missing, setMissing] = useState(false)
  const [step, setStep] = useState<Step>({ kind: 'idle' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const setLang = (l: OfferLang) => {
    setLangState(l)
    try {
      localStorage.setItem('offer-lang', l)
    } catch {
      // Language just for this visit.
    }
  }

  useEffect(() => {
    fetchOffer(id)
      .then(setOffer)
      .catch(() => setMissing(true))
  }, [id])

  useEffect(() => {
    document.title = offer ? `${offer.doc.ref} · ${offer.doc.member}` : 'Freight offer'
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en'
  }, [offer, lang])

  const submit = useCallback(
    async (d: Omit<OfferDecision, 'at'>) => {
      setBusy(true)
      setError('')
      try {
        setOffer(await sendDecision(id, d))
        setStep({ kind: 'idle' })
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (e) {
        if (e instanceof Error && e.message.startsWith('already')) {
          setOffer(await fetchOffer(id, true))
          setStep({ kind: 'idle' })
        } else setError(t.error)
      } finally {
        setBusy(false)
      }
    },
    [id, t.error]
  )

  const top = (
    <header className="op-top">
      <div className="op-brand">{brand.mark(offer?.doc ?? null)}</div>
      <fieldset className="op-lang" aria-label="Language">
        <button type="button" aria-pressed={lang === 'zh'} onClick={() => setLang('zh')}>
          中文
        </button>
        <button type="button" aria-pressed={lang === 'en'} onClick={() => setLang('en')}>
          EN
        </button>
      </fieldset>
    </header>
  )

  if (missing)
    return (
      <div className="op">
        {top}
        <main className="op-main">
          <section className="op-card op-empty">
            <h1>{t.notFound}</h1>
            <p>{t.notFoundBody}</p>
          </section>
        </main>
        <style>{styles}</style>
      </div>
    )
  if (!offer)
    return (
      <div className="op">
        {top}
        <main className="op-main">
          <p className="op-loading">{t.loading}</p>
        </main>
        <style>{styles}</style>
      </div>
    )

  const d = offer.doc
  const [from = '', to = ''] = d.route.split('→').map((s) => s.trim())
  const daysLeft = Math.ceil((Date.parse(d.validUntil) - Date.now()) / 864e5)
  const decision = offer.decision
  const open = offer.status === 'live' || offer.status === 'viewed'

  return (
    <div className="op">
      {top}
      <main className="op-main">
        {d.sample && !decision && <p className="op-sample">{t.sample}</p>}

        <section className="op-hero">
          <span className="op-kicker">
            {t.kicker} · {d.ref}
          </span>
          <h1>
            <span>
              {portName(from, lang, portNames)}
              <small>{from}</small>
            </span>
            <span className="op-arrow" aria-hidden="true">
              →
            </span>
            <span>
              {portName(to, lang, portNames)}
              <small>{to}</small>
            </span>
          </h1>
          <p>
            {t.for} <b>{d.client}</b> · {t.from} {d.seller.name}
            {lang === 'en' ? ` ${t.at} ${d.member}` : `（${d.member}）`}
            {d.equipment ? ` · ${d.equipment}` : ''}
          </p>
          {open && (
            <span className="op-valid">
              {t.validUntil} {fmtDate(d.validUntil, lang)} · {t.daysLeft(daysLeft)}
            </span>
          )}
        </section>

        {decision && (
          <section className={`op-card op-done ${decision.kind}`}>
            <div className="op-done-head">
              <span className="op-done-mark" aria-hidden="true">
                {decision.kind === 'confirmed' ? '✓' : '×'}
              </span>
              <div>
                <h2>{decision.kind === 'confirmed' ? t.confirmed : t.declined}</h2>
                <p>{t.notified(d.member)}</p>
              </div>
            </div>
            <dl>
              <dt>{t.by}</dt>
              <dd>
                {decision.name}
                {decision.company ? `, ${decision.company}` : ''} ({t.via[decision.via]})
              </dd>
              <dt>{t.when}</dt>
              <dd>{fmtTime(decision.at, lang)}</dd>
              <dt>{t.ref}</dt>
              <dd>
                {d.ref} · {fmtMoney(d.total, d.currency)}
              </dd>
              {decision.po && (
                <>
                  <dt>{t.poShort}</dt>
                  <dd>{decision.po}</dd>
                </>
              )}
              {decision.reason && (
                <>
                  <dt>{t.reason}</dt>
                  <dd>{decision.reason}</dd>
                </>
              )}
            </dl>
            <button type="button" className="op-ghost" onClick={() => window.print()}>
              {t.print}
            </button>
          </section>
        )}

        {offer.status === 'expired' && (
          <section className="op-card op-empty">
            <h2>{t.expired}</h2>
            <p>{t.expiredBody(d.seller.name)}</p>
          </section>
        )}

        <section className="op-card">
          <h2 className="op-h">{t.charges}</h2>
          <ul className="op-lines">
            {d.lines.map((l, i) => {
              const zh = lang === 'zh' ? chargeZh(l.name) : undefined
              return (
                <li key={`${l.name}-${i}`}>
                  <span>
                    {zh ?? l.name}
                    {zh && <small>{l.name}</small>}
                  </span>
                  <b>{fmtMoney(l.amount, d.currency)}</b>
                </li>
              )
            })}
          </ul>
          <div className="op-total">
            <span>{t.total}</span>
            <strong>{fmtMoney(d.total, d.currency)}</strong>
          </div>
        </section>

        {open && (
          <section className="op-card">
            <h2 className="op-h">{t.next}</h2>
            <ol className="op-steps">
              {t.steps(d.member).map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </section>
        )}

        <p className="op-contact">
          {t.contact} {d.seller.name}
          {d.seller.email && (
            <>
              {' '}
              ·{' '}
              <a href={`mailto:${d.seller.email}?subject=${encodeURIComponent(d.ref)}`}>
                {d.seller.email}
              </a>
            </>
          )}
          {d.seller.wechat && <> · WeChat {d.seller.wechat}</>}
        </p>
        <p className="op-powered">{brand.powered[lang]}</p>
      </main>

      {open && step.kind === 'idle' && (
        <div className="op-bar">
          <div>
            <small>{t.total}</small>
            <strong>{fmtMoney(d.total, d.currency)}</strong>
          </div>
          <button
            type="button"
            className="op-secondary"
            onClick={() => setStep({ kind: 'who', action: 'declined' })}
          >
            {t.decline}
          </button>
          <button
            type="button"
            className="op-primary"
            onClick={() => setStep({ kind: 'who', action: 'confirmed' })}
          >
            {t.confirm}
          </button>
        </div>
      )}

      {open && step.kind !== 'idle' && (
        <DecisionSheet
          lang={lang}
          step={step}
          setStep={setStep}
          client={d.client}
          member={d.member}
          brand={brand}
          busy={busy}
          error={error}
          onSubmit={submit}
        />
      )}
      <style>{styles}</style>
    </div>
  )
}

function DecisionSheet({
  lang,
  step,
  setStep,
  client,
  member,
  brand,
  busy,
  error,
  onSubmit,
}: {
  lang: OfferLang
  step: Exclude<Step, { kind: 'idle' }>
  setStep: (s: Step) => void
  client: string
  member: string
  brand: OfferPageBrand
  busy: boolean
  error: string
  onSubmit: (d: Omit<OfferDecision, 'at'>) => void
}) {
  const t = T[lang]
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState(client)
  const [wxName, setWxName] = useState(lang === 'zh' ? '王先生' : 'Mr Wang')
  const [po, setPo] = useState('')
  const [reason, setReason] = useState(t.reasons[0] ?? '')
  const [note, setNote] = useState('')
  const action = step.action
  const close = () => setStep({ kind: 'idle' })
  const emailOk = name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

  return (
    <div className="op-scrim" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <dialog open className="op-sheet" aria-label={action === 'confirmed' ? t.confirm : t.decline}>
        {step.kind === 'who' && (
          <>
            <h2>{action === 'confirmed' ? t.who : t.whoDecline}</h2>
            <button
              type="button"
              className="op-wechat"
              onClick={() => setStep({ kind: 'wechat', action })}
            >
              <WeChatGlyph size={20} /> {t.withWechat}
            </button>
            <p className="op-or">{t.orEmail}</p>
            <label>
              <span>{t.name}</span>
              <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </label>
            <label>
              <span>{t.company}</span>
              <input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                autoComplete="organization"
              />
            </label>
            <label>
              <span>{t.email}</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>
            <div className="op-sheet-actions">
              <button type="button" className="op-secondary" onClick={close}>
                {t.back}
              </button>
              <button
                type="button"
                className="op-primary"
                disabled={!emailOk}
                onClick={() =>
                  setStep({
                    kind: 'details',
                    action,
                    id: {
                      via: 'email',
                      name: name.trim(),
                      email: email.trim(),
                      company: company.trim(),
                    },
                  })
                }
              >
                →
              </button>
            </div>
          </>
        )}

        {step.kind === 'wechat' && (
          <div className="op-wx">
            <div className="op-wx-head">
              <WeChatGlyph size={18} /> {t.wxTitle}
            </div>
            <div className="op-wx-app">
              <span className="op-wx-logo">{member.slice(0, 2)}</span>
              <b>{brand.app(member)}</b>
              <p>{t.wxAsks(brand.app(member))}</p>
            </div>
            <label>
              <span>{t.wxName}</span>
              <input value={wxName} onChange={(e) => setWxName(e.target.value)} />
            </label>
            <button
              type="button"
              className="op-wechat"
              disabled={!wxName.trim()}
              onClick={() =>
                setStep({ kind: 'details', action, id: { via: 'wechat', name: wxName.trim() } })
              }
            >
              {t.wxAllow}
            </button>
            <button
              type="button"
              className="op-link"
              onClick={() => setStep({ kind: 'who', action })}
            >
              {t.wxDeny}
            </button>
            <small className="op-wx-sim">{brand.simulated[lang]}</small>
          </div>
        )}

        {step.kind === 'details' && (
          <>
            <h2>{action === 'confirmed' ? t.confirm : t.decline}</h2>
            <p className="op-who">
              {step.id.via === 'wechat' ? (
                <>
                  <span className="op-wx-dot" aria-hidden="true">
                    <WeChatGlyph size={12} />
                  </span>
                  {t.signedIn} <b>{step.id.name}</b>
                </>
              ) : (
                <>
                  <b>{step.id.name}</b> · {step.id.email}
                </>
              )}
            </p>
            {action === 'confirmed' ? (
              <label>
                <span>{t.po}</span>
                <input value={po} onChange={(e) => setPo(e.target.value)} />
              </label>
            ) : (
              <>
                <fieldset className="op-reasons">
                  <legend>{t.reason}</legend>
                  {t.reasons.map((r) => (
                    <button
                      type="button"
                      key={r}
                      aria-pressed={reason === r}
                      onClick={() => setReason(r)}
                    >
                      {r}
                    </button>
                  ))}
                </fieldset>
                <label>
                  <span>{t.reasonNote}</span>
                  <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
                </label>
              </>
            )}
            {error && <p className="op-error">{error}</p>}
            <div className="op-sheet-actions">
              <button
                type="button"
                className="op-secondary"
                onClick={() => setStep({ kind: 'who', action })}
              >
                {t.back}
              </button>
              <button
                type="button"
                className={action === 'confirmed' ? 'op-primary' : 'op-danger'}
                disabled={busy}
                onClick={() =>
                  onSubmit({
                    kind: action,
                    via: step.id.via,
                    name: step.id.name,
                    ...(step.id.via === 'wechat'
                      ? { wechatName: step.id.name, company: client }
                      : { email: step.id.email, company: step.id.company || client }),
                    ...(action === 'confirmed' && po.trim() ? { po: po.trim() } : {}),
                    ...(action === 'declined'
                      ? { reason: [reason, note.trim()].filter(Boolean).join(': ').slice(0, 300) }
                      : {}),
                  })
                }
              >
                {busy ? t.sending : action === 'confirmed' ? t.send : t.sendDecline}
              </button>
            </div>
          </>
        )}
      </dialog>
    </div>
  )
}

const styles = `
:root{--op-ink:#0b0c0e;--op-ink-2:#525e6b;--op-ink-3:#788392;--op-line:rgb(14 20 27/.1);--op-bg:#f2f4f7;--op-card:#fff;--op-accent:#1d5bd6;--op-good:#047857;--op-bad:#b91c1c}
@media (prefers-color-scheme: dark){:root:not([data-theme="light"]){--op-ink:#eef1f5;--op-ink-2:#b4bcc7;--op-ink-3:#8b95a3;--op-line:rgb(255 255 255/.1);--op-bg:#0d1117;--op-card:#161b22;--op-accent:#6c9bff;--op-good:#34d399;--op-bad:#f87171}}
body{margin:0;background:var(--op-bg)}
.op{min-height:100vh;background:var(--op-bg);color:var(--op-ink);font-family:Switzer,'PingFang SC','Microsoft YaHei',ui-sans-serif,system-ui,sans-serif;padding-bottom:110px}
.op *{box-sizing:border-box}.op button,.op input,.op textarea{font:inherit;color:inherit}
.op-top{display:flex;align-items:center;justify-content:space-between;gap:12px;max-width:720px;margin:0 auto;padding:16px}
.op-brand{display:flex;flex-wrap:wrap;align-items:center;gap:8px;min-width:0}.op-brand b{font-family:Ranade,Georgia,serif;font-size:22px;color:var(--op-accent);letter-spacing:-.02em}.op-brand i{font-style:normal;color:var(--op-ink-3)}
.op-member{font-weight:700;font-size:14px;border:1px solid var(--op-line);border-radius:8px;padding:3px 8px;background:var(--op-card)}
.op-lang{display:flex;flex:none;border:0;margin:0;padding:3px;background:rgb(14 20 27/.06);border-radius:10px}
.op-lang button{border:0;background:none;padding:5px 11px;border-radius:7px;font-size:13px;font-weight:600;color:var(--op-ink-2);cursor:pointer;white-space:nowrap}
.op-lang button[aria-pressed="true"]{background:var(--op-card);color:var(--op-ink);box-shadow:0 1px 3px rgb(0 0 0/.12)}
.op-main{max-width:720px;margin:0 auto;padding:0 16px;display:grid;gap:14px}
.op-loading{color:var(--op-ink-3);padding:40px 0;text-align:center}
.op-sample{margin:0;padding:10px 14px;border-radius:12px;background:color-mix(in srgb,#f59e0b 14%,var(--op-card));color:var(--op-ink);font-size:13px;line-height:1.5}
.op-hero{padding:10px 2px 4px}
.op-kicker{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--op-accent);font-weight:700}
.op-hero h1{display:flex;flex-wrap:wrap;align-items:center;gap:6px 14px;margin:10px 0 8px;font-family:Ranade,'PingFang SC',Georgia,serif;font-size:clamp(28px,7vw,40px);letter-spacing:-.03em;line-height:1.1;font-weight:600}
.op-hero h1 span{display:grid}.op-hero h1 small{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:12px;letter-spacing:.06em;color:var(--op-ink-3);font-weight:500}
.op-hero h1 .op-arrow{display:inline;color:var(--op-ink-3);font-size:.8em}
.op-hero p{margin:0;color:var(--op-ink-2);font-size:14.5px;line-height:1.5}
.op-valid{display:inline-block;margin-top:12px;padding:6px 11px;border-radius:999px;background:color-mix(in srgb,var(--op-good) 12%,var(--op-card));color:var(--op-good);font-size:13px;font-weight:600}
.op-card{background:var(--op-card);border:1px solid var(--op-line);border-radius:18px;padding:18px;box-shadow:0 12px 30px -24px rgb(20 32 48/.35)}
.op-h{margin:0 0 10px;font-size:13px;font-weight:700;color:var(--op-ink-2)}
.op-lines{list-style:none;margin:0;padding:0}
.op-lines li{display:flex;justify-content:space-between;gap:14px;padding:11px 0;border-top:1px solid var(--op-line);font-size:15px}
.op-lines li:first-child{border-top:0}
.op-lines span{display:grid}.op-lines small{font-size:12px;color:var(--op-ink-3)}
.op-lines b{font-weight:600;font-variant-numeric:tabular-nums;white-space:nowrap}
.op-total{display:flex;justify-content:space-between;align-items:baseline;margin-top:6px;padding-top:12px;border-top:2px solid var(--op-ink)}
.op-total span{font-weight:700}.op-total strong{font-size:24px;font-variant-numeric:tabular-nums;letter-spacing:-.01em}
.op-steps{margin:0;padding-left:20px;list-style:decimal;display:grid;gap:7px;color:var(--op-ink-2);font-size:14.5px;line-height:1.5}
.op-contact{margin:4px 2px 0;font-size:13px;color:var(--op-ink-2)}.op-contact a{color:var(--op-accent)}
.op-powered{margin:0 2px;font-size:12px;color:var(--op-ink-3)}
.op-empty h1,.op-empty h2{margin:0 0 6px;font-size:20px}.op-empty p{margin:0;color:var(--op-ink-2);line-height:1.5}
.op-done{border-width:1.5px}.op-done.confirmed{border-color:color-mix(in srgb,var(--op-good) 45%,transparent)}.op-done.declined{border-color:color-mix(in srgb,var(--op-bad) 40%,transparent)}
.op-done-head{display:flex;gap:14px;align-items:center}
.op-done-mark{display:grid;place-items:center;width:44px;height:44px;border-radius:50%;font-size:22px;font-weight:700;color:#fff;flex:none}
.op-done.confirmed .op-done-mark{background:var(--op-good)}.op-done.declined .op-done-mark{background:var(--op-bad)}
.op-done h2{margin:0;font-size:20px}.op-done-head p{margin:2px 0 0;color:var(--op-ink-2);font-size:13.5px}
.op-done dl{display:grid;grid-template-columns:auto 1fr;gap:8px 16px;margin:16px 0;font-size:14px}
.op-done dt{color:var(--op-ink-3)}.op-done dd{margin:0;font-weight:600}
.op-ghost{border:1px solid var(--op-line);background:none;border-radius:11px;padding:10px 14px;font-weight:600;cursor:pointer}
.op-bar{position:fixed;left:0;right:0;bottom:0;z-index:20;display:grid;grid-template-columns:1fr auto auto;gap:10px;align-items:center;padding:12px max(16px,calc((100vw - 720px)/2 + 16px)) calc(12px + env(safe-area-inset-bottom));background:color-mix(in srgb,var(--op-card) 92%,transparent);backdrop-filter:blur(12px);border-top:1px solid var(--op-line)}
.op-bar small{display:block;font-size:12px;color:var(--op-ink-3)}.op-bar strong{font-size:18px;font-variant-numeric:tabular-nums}
.op-primary,.op-secondary,.op-danger,.op-wechat{border:0;border-radius:13px;padding:13px 18px;font-weight:700;font-size:15px;cursor:pointer}
.op-primary{background:var(--op-accent);color:#fff}.op-secondary{background:rgb(14 20 27/.06)}.op-danger{background:var(--op-bad);color:#fff}
.op-primary:disabled,.op-wechat:disabled,.op-danger:disabled{opacity:.5;cursor:default}
.op-wechat{display:flex;align-items:center;justify-content:center;gap:9px;width:100%;background:${WECHAT_GREEN};color:#fff}
.op-scrim{position:fixed;inset:0;z-index:40;display:flex;align-items:flex-end;justify-content:center;background:rgb(10 14 20/.45)}
.op-sheet{position:relative;margin:0;width:min(520px,100%);max-height:92vh;overflow:auto;border:0;border-radius:22px 22px 0 0;background:var(--op-card);color:var(--op-ink);padding:22px 20px calc(20px + env(safe-area-inset-bottom));display:grid;gap:12px}
@media(min-width:640px){.op-scrim{align-items:center}.op-sheet{border-radius:22px}}
.op-sheet h2{margin:0 0 2px;font-size:20px}
.op-sheet label{display:grid;gap:5px}.op-sheet label span,.op-reasons legend{font-size:13px;color:var(--op-ink-2);font-weight:600}
.op-sheet input,.op-sheet textarea{border:1px solid var(--op-line);background:var(--op-bg);border-radius:11px;padding:11px 12px;font-size:15px;outline:none;width:100%}
.op-sheet input:focus,.op-sheet textarea:focus{border-color:var(--op-accent)}
.op-or{margin:4px 0 0;text-align:center;font-size:13px;color:var(--op-ink-3)}
.op-sheet-actions{display:grid;grid-template-columns:auto 1fr;gap:10px;margin-top:4px}
.op-who{margin:0;display:flex;align-items:center;gap:7px;font-size:14px;color:var(--op-ink-2)}
.op-wx-dot{display:grid;place-items:center;width:20px;height:20px;border-radius:6px;background:${WECHAT_GREEN};color:#fff}
.op-reasons{border:0;margin:0;padding:0;display:flex;flex-wrap:wrap;gap:7px}.op-reasons legend{margin-bottom:7px}
.op-reasons button{border:1px solid var(--op-line);background:none;border-radius:999px;padding:7px 12px;font-size:13.5px;cursor:pointer}
.op-reasons button[aria-pressed="true"]{border-color:var(--op-ink);background:var(--op-ink);color:var(--op-card)}
.op-error{margin:0;color:var(--op-bad);font-size:13.5px}
.op-wx{display:grid;gap:14px;text-align:center}
.op-wx-head{display:flex;align-items:center;justify-content:center;gap:7px;font-weight:700;color:${WECHAT_GREEN}}
.op-wx-app{display:grid;justify-items:center;gap:6px;padding:8px 0}
.op-wx-logo{display:grid;place-items:center;width:56px;height:56px;border-radius:14px;background:var(--op-accent);color:#fff;font-weight:800;font-size:18px}
.op-wx-app p{margin:0;font-size:14px;color:var(--op-ink-2);line-height:1.5;max-width:340px}
.op-wx label{text-align:left}
.op-link{border:0;background:none;color:var(--op-ink-2);font-weight:600;cursor:pointer;padding:4px}
.op-wx-sim{color:var(--op-ink-3);font-size:12px;line-height:1.5}
@media print{.op-bar,.op-lang,.op-ghost,.op-scrim{display:none!important}.op{padding:0;background:#fff}.op-card{box-shadow:none}}
`
